// src/components/Chat/Message/MessageFeedback.tsx
import React from "react";
import { useTheme } from "@mui/material/styles";
import { Box, Tooltip, Typography } from "@mui/material";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import GraphicEqOutlinedIcon from "@mui/icons-material/GraphicEqOutlined";
import PlayCircleOutlinedIcon from "@mui/icons-material/PlayCircleOutlined";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";

interface MessageFeedbackProps {
  onFeedbackMessage: (chatId: string, msgId: string) => void;
  onCopy: (msgId: string, content: string) => void;
  msgId: string;
  chatId: string | null;
  content: string;
  isCopied: boolean;
  models: {
    [key: string]: { name: string; model: string; description: string };
  };
  model: string;
  databases: {
    [key: string]: { name: string; database: string; type: string };
  };
  database: string;
  styles: {
    [key: string]: { name: string; style: string; type: string };
  };
  style: string;
  isGenerating: boolean;
  speechStatus: string;
  speechStart: any;
  speechStop: any;
}

// New MessageFeedback component to handle individual message actions
const MessageFeedback: React.FC<MessageFeedbackProps> = ({
  onFeedbackMessage,
  onCopy,
  msgId,
  chatId,
  content,
  isCopied,
  models,
  model,
  databases,
  database,
  styles,
  style,
  isGenerating,
  speechStatus,
  speechStart,
  speechStop,
}) => {
  const theme = useTheme();

  const modelName = models[model]?.name;
  const databaseName = databases[database]?.name;
  const styleName = styles[style]?.name;

  return (
    <Box
      display="flex"
      justifyContent="flex-start"
      alignItems="center"
      mt={2}
      gap={2}
    >
      {isCopied ? (
        <CheckCircleIcon
          sx={{
            fontSize: 16,
            color: theme.palette.success.main,
          }}
        />
      ) : (
        <Tooltip title="คัดลอก">
          <ContentCopyOutlinedIcon
            onClick={() => !isGenerating && onCopy(msgId, content)}
            sx={{
              cursor: !isGenerating ? "pointer" : "not-allowed",
              fontSize: 16,
              color: !isGenerating ? "white" : "white",
            }}
          />
        </Tooltip>
      )}
      <Tooltip title="ให้คำติชม">
        <FeedbackOutlinedIcon
          onClick={() => {
            chatId && !isGenerating && onFeedbackMessage(chatId, msgId);
          }}
          sx={{
            cursor: chatId && !isGenerating ? "pointer" : "not-allowed",
            fontSize: 16,
            color: chatId && !isGenerating ? "white" : "white",
          }}
        />
      </Tooltip>
      {speechStatus === "started" ? (
        <Tooltip title="อ่านให้ฟัง">
          <Box
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            sx={{ cursor: "pointer", gap: 1 }}
            onClick={() => speechStop()}
          >
            <StopCircleOutlinedIcon
              sx={{
                fontSize: 16,
                color: theme.palette.warning.main,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                WebkitLineClamp: 2,
                whiteSpace: "normal",
                color: theme.palette.warning.main,
              }}
            >
              หยุดการเล่น
            </Typography>
          </Box>
        </Tooltip>
      ) : (
        <Tooltip title="อ่านให้ฟัง">
          <Box
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            sx={{ cursor: !isGenerating ? "pointer" : "not-allowed", gap: 1 }}
            onClick={() => !isGenerating && speechStart()}
          >
            <PlayCircleOutlinedIcon
              sx={{
                fontSize: 16,
                color: !isGenerating ? "white" : "white",
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                WebkitLineClamp: 2,
                whiteSpace: "normal",
                color: !isGenerating ? "white" : "white",
              }}
            >
              เล่นเสียง
            </Typography>
          </Box>
        </Tooltip>
      )}
      {/* {modelName && (
        <Tooltip title="โมเดล">
          <Box
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            sx={{ cursor: "default", gap: 1 }}
          >
            <AutoAwesomeOutlinedIcon
              sx={{
                fontSize: 16,
                color: "primary.main",
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                WebkitLineClamp: 2,
                whiteSpace: "normal",
              }}
            >
              {modelName}
            </Typography>
          </Box>
        </Tooltip>
      )} */}
      {databaseName && (
        <Tooltip title="หมวดหมู่ข้อมูล">
          <Box
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            sx={{ cursor: "default", gap: 1 }}
          >
            <StorageOutlinedIcon
              sx={{
                fontSize: 16,
                color: "primary.main",
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                WebkitLineClamp: 2,
                whiteSpace: "normal",
              }}
            >
              {databaseName}
            </Typography>
          </Box>
        </Tooltip>
      )}
      {/* {styleName && (
        <Tooltip title="ลักษณะการตอบคำถาม">
          <Box
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            sx={{ cursor: "default", gap: 1 }}
          >
            <GraphicEqOutlinedIcon
              sx={{
                fontSize: 16,
                color: "primary.main",
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                WebkitLineClamp: 2,
                whiteSpace: "normal",
              }}
            >
              {styleName}
            </Typography>
          </Box>
        </Tooltip>
      )} */}
    </Box>
  );
};

export default MessageFeedback;
